import React, { Fragment, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap";
import { Breadcrumbs, H2, P } from "../../../../../AbstractElements";

const SAMPLE_TEXT = "";

const columns = [
  "Date",
  "Customer",
  "Project",
  "Task/Module",
  "Subject",
  "Tool",
  "Source",
  "Start",
  "End",
  "Duration HH:MM",
  "Duration decimal hours",
  "Location",
  "Ref #",
  "Billable",
  "Tags",
  "Comments",
  "Internal comments"
];

function parseCsvRows(csv) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index];
    const next = csv[index + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (char !== "\r") {
      field += char;
    }
  }

  row.push(field);
  if (row.length > 1 || row[0] !== "") {
    rows.push(row);
  }

  const [headers = [], ...dataRows] = rows;
  return dataRows.map((dataRow) =>
    Object.fromEntries(headers.map((header, index) => [header, dataRow[index] || ""]))
  );
}

function parseDurationSeconds(value) {
  const [hours = "0", minutes = "0"] = String(value || "0:00").split(":");
  return (Number(hours) * 60 + Number(minutes)) * 60;
}

function formatDuration(seconds) {
  const minutes = Math.round(seconds / 60);
  const hoursPart = Math.floor(minutes / 60);
  const minutesPart = minutes % 60;
  return `${String(hoursPart).padStart(2, "0")}:${String(minutesPart).padStart(2, "0")}`;
}

function csvEscape(value) {
  const text = value == null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function rowsToCsv(rows) {
  return [
    columns.join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column])).join(","))
  ].join("\n");
}

function sessionsToRows(sessions) {
  return sessions.map((session) => ({
    Date: String(session.startAt || "").slice(0, 10),
    Customer: session.customer || "",
    Project: session.externalProject || session.projectName || "",
    "Task/Module": session.externalTask || "",
    Subject: session.subject || "",
    Tool: session.tool || "",
    Source: session.source || "",
    Start: String(session.startAt || "").slice(11, 16),
    End: String(session.endAt || "").slice(11, 16),
    "Duration HH:MM": formatDuration(session.activeDurationSeconds || session.durationSeconds || 0),
    "Duration decimal hours": ((session.activeDurationSeconds || session.durationSeconds || 0) / 3600).toFixed(2),
    Location: session.location || "",
    "Ref #": session.refNumber || "",
    Billable: session.billable === false ? "No" : "Yes",
    Tags: Array.isArray(session.tags) ? session.tags.join("; ") : "",
    Comments: session.comments || "",
    "Internal comments": session.internalComments || ""
  }));
}

function parseImport(text, format) {
  if (!text.trim()) {
    return [];
  }

  if (format === "json") {
    const parsed = JSON.parse(text);
    const sessions = Array.isArray(parsed) ? parsed : parsed.sessions;
    if (!Array.isArray(sessions)) {
      throw new Error("JSON must be an array or an object with a sessions array.");
    }
    return sessionsToRows(sessions);
  }

  return parseCsvRows(text);
}

function summarize(rows) {
  const byProject = new Map();
  let totalSeconds = 0;

  rows.forEach((row) => {
    const project = row.Project || "Unassigned";
    const seconds = parseDurationSeconds(row["Duration HH:MM"]);
    totalSeconds += seconds;
    const current = byProject.get(project) || { project, seconds: 0, rows: 0 };
    current.seconds += seconds;
    current.rows += 1;
    byProject.set(project, current);
  });

  return {
    totalSeconds,
    byProject: [...byProject.values()].sort((a, b) => b.seconds - a.seconds)
  };
}

const CodingWorkTimesheet = () => {
  const [format, setFormat] = useState("csv");
  const [text, setText] = useState(SAMPLE_TEXT);
  const [copied, setCopied] = useState(false);

  const parsedImport = useMemo(() => {
    try {
      return { rows: parseImport(text, format), error: "" };
    } catch (parseError) {
      return { rows: [], error: parseError.message };
    }
  }, [format, text]);
  const rows = parsedImport.rows;
  const error = parsedImport.error;
  const summary = useMemo(() => summarize(rows), [rows]);
  const csv = useMemo(() => rowsToCsv(rows), [rows]);

  const copyCsv = async () => {
    await navigator.clipboard.writeText(csv);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Coding Work" parent="Utilisateurs" title="Feuille de temps Coding Work" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Row>
                  <Col md="8">
                    <H2 attrH2={{ className: "title" }}>Feuille de temps Coding Work</H2>
                    <P>Import local du fichier CSV ou JSON genere par Timetracker. Aucune ecriture Firebase n'est effectuee.</P>
                  </Col>
                  <Col md="4" className="text-md-end">
                    <Button color="primary" disabled={!rows.length} onClick={copyCsv}>
                      {copied ? "Copie" : "Copier CSV MaxPark"}
                    </Button>
                  </Col>
                </Row>

                <Row className="m-t-20">
                  <Col md="3">
                    <label className="form-label">Format</label>
                    <select className="form-control" value={format} onChange={(event) => setFormat(event.target.value)}>
                      <option value="csv">CSV</option>
                      <option value="json">JSON</option>
                    </select>
                  </Col>
                  <Col md="9">
                    <label className="form-label">Import Timetracker</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      placeholder="Coller le CSV ou JSON Timetracker ici"
                    />
                  </Col>
                </Row>

                {error ? <P attrPara={{ className: "text-danger m-t-20" }}>{error}</P> : ""}

                <Row className="m-t-20">
                  <Col md="4">
                    <Card className="shadow-none border">
                      <CardBody>
                        <P>Total importe</P>
                        <H2>{formatDuration(summary.totalSeconds)}</H2>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="shadow-none border">
                      <CardBody>
                        <P>Heures decimales</P>
                        <H2>{(summary.totalSeconds / 3600).toFixed(2)}</H2>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="shadow-none border">
                      <CardBody>
                        <P>Lignes</P>
                        <H2>{rows.length}</H2>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="m-t-20">
                  <Col md="12">
                    <div className="table-responsive">
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Projet</th>
                            <th>Duree</th>
                            <th>Lignes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.byProject.map((project) => (
                            <tr key={project.project}>
                              <td>{project.project}</td>
                              <td>{formatDuration(project.seconds)}</td>
                              <td>{project.rows}</td>
                            </tr>
                          ))}
                          {!summary.byProject.length && (
                            <tr>
                              <td colSpan="3">Aucune donnee importee.</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>

                <Row className="m-t-20">
                  <Col md="12">
                    <label className="form-label">CSV normalise</label>
                    <textarea className="form-control" rows="8" value={csv} readOnly />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CodingWorkTimesheet;
